import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Loader2, KeyRound, Copy, X, AlertTriangle, RefreshCw } from "lucide-react";
import { cn } from "@/lib/util";

/**
 * MFA (TOTP) enrollment + disable panel. Se monta dentro de /settings.
 * Flujo:
 *   status → enroll (QR + secret) → verify-enroll (código) → backup_codes
 *   status enabled → disable (password + code)
 */
export default function MFASection() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [enrolling, setEnrolling] = useState(null); // {secret, qr_png, otpauth_uri}
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState(null);
  const [disableOpen, setDisableOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [dCode, setDCode] = useState("");
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenPw, setRegenPw] = useState("");
  const [regenCode, setRegenCode] = useState("");

  const load = async () => {
    try { const r = await api.get("/auth/mfa/status"); setStatus(r.data); }
    catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const startEnroll = async () => {
    setBusy(true);
    try { const r = await api.post("/auth/mfa/enroll"); setEnrolling(r.data); setCode(""); setBackupCodes(null); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo iniciar enrollment"); }
    finally { setBusy(false); }
  };

  const confirmEnroll = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/mfa/verify-enroll", { code });
      setBackupCodes(r.data.backup_codes);
      setEnrolling(null);
      toast.success("MFA activado");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Código inválido"); }
    finally { setBusy(false); }
  };

  const regenerate = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/mfa/regenerate-backup-codes",
        { password: regenPw, code: regenCode });
      setBackupCodes(r.data.backup_codes);
      setRegenOpen(false); setRegenPw(""); setRegenCode("");
      toast.success("Códigos regenerados. Los anteriores ya no funcionan.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo regenerar"); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/mfa/disable", { password: pw, code: dCode });
      toast.success("MFA deshabilitado");
      setDisableOpen(false); setPw(""); setDCode(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo deshabilitar"); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-border p-5 bg-card/50" data-testid="mfa-section">
      <div className="flex items-start justify-between mb-4 gap-4">
        <div>
          <div className="overline flex items-center gap-2 text-primary">
            <ShieldCheck className="w-3 h-3" /> Autenticación de dos factores (TOTP)
          </div>
          <h3 className="font-display text-xl font-medium mt-1">Google/Microsoft Authenticator</h3>
          <p className="text-xs text-muted-foreground mt-1 max-w-lg">
            Añade un código de 6 dígitos generado por tu app al inicio de sesión. Los secretos se cifran en la base de datos con AES-128 (Fernet).
          </p>
        </div>
        <span className={cn(
          "font-mono text-[10px] tracking-widest uppercase px-2 py-1 border",
          status?.mfa_enabled
            ? "border-sev-low/50 text-sev-low bg-sev-low/10"
            : "border-border text-muted-foreground"
        )} data-testid="mfa-badge">
          {status?.mfa_enabled ? "ACTIVO" : "INACTIVO"}
        </span>
      </div>

      {/* No enrolado + no enrolling */}
      {!status?.mfa_enabled && !enrolling && !backupCodes && (
        <button onClick={startEnroll} disabled={busy}
                data-testid="mfa-enroll-btn"
                className="bg-primary text-primary-foreground px-4 py-2 text-sm font-mono tracking-widest uppercase hover:bg-primary/90 disabled:opacity-50 inline-flex items-center gap-2">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <KeyRound className="w-3 h-3" />}
          Activar MFA
        </button>
      )}

      {/* Enrolling: QR + código */}
      {enrolling && (
        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 items-start" data-testid="mfa-enroll-panel">
          <div className="border border-border bg-white p-2 w-fit">
            <img src={enrolling.qr_png} alt="QR MFA" className="w-48 h-48" data-testid="mfa-qr" />
          </div>
          <div className="space-y-3">
            <div>
              <div className="overline mb-1">Secreto (introdúcelo manualmente si no puedes escanear)</div>
              <div className="font-mono text-sm bg-background border border-border p-2 flex items-center justify-between gap-2">
                <span data-testid="mfa-secret">{enrolling.secret}</span>
                <button onClick={() => { navigator.clipboard.writeText(enrolling.secret); toast.success("Secreto copiado"); }}
                        className="p-1 hover:bg-muted"><Copy className="w-3 h-3" /></button>
              </div>
            </div>
            <div>
              <div className="overline mb-1">Introduce el código de 6 dígitos</div>
              <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                     placeholder="123456"
                     data-testid="mfa-verify-input"
                     className="w-32 bg-background border border-border px-3 py-2 text-lg font-mono tracking-widest text-center focus:border-primary outline-none" />
            </div>
            <div className="flex gap-2">
              <button onClick={confirmEnroll} disabled={busy || code.length !== 6}
                      data-testid="mfa-verify-btn"
                      className="bg-primary text-primary-foreground px-4 py-2 text-sm font-mono tracking-widest uppercase hover:bg-primary/90 disabled:opacity-50">
                Confirmar
              </button>
              <button onClick={() => { setEnrolling(null); setCode(""); }}
                      className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Backup codes tras enroll exitoso */}
      {backupCodes && (
        <div className="border border-sev-medium/40 bg-sev-medium/10 p-4" data-testid="mfa-backup-codes">
          <div className="flex items-center gap-2 text-sev-medium overline mb-2">
            <AlertTriangle className="w-3 h-3" /> Guarda estos códigos de respaldo — no se mostrarán de nuevo
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 font-mono text-xs">
            {backupCodes.map(c => <div key={c} className="bg-background border border-border p-2 text-center">{c}</div>)}
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={() => { navigator.clipboard.writeText(backupCodes.join("\n")); toast.success("Códigos copiados"); }}
                    className="text-xs font-mono tracking-widest uppercase border border-border px-3 py-1 hover:border-primary hover:text-primary inline-flex items-center gap-1">
              <Copy className="w-3 h-3" /> Copiar todos
            </button>
            <button onClick={() => setBackupCodes(null)}
                    className="text-xs font-mono tracking-widest uppercase border border-border px-3 py-1 hover:border-foreground">
              Entendido, ya los guardé
            </button>
          </div>
        </div>
      )}

      {/* Ya activo → regenerar / disable */}
      {status?.mfa_enabled && !disableOpen && !regenOpen && !backupCodes && (
        <div className="flex flex-wrap items-baseline justify-between gap-4">
          <div className="text-xs text-muted-foreground font-mono">
            Activado el {status.mfa_enabled_at ? new Date(status.mfa_enabled_at).toLocaleString() : "—"} ·{" "}
            <span
              data-testid="mfa-backup-remaining"
              className={cn(
                (status.backup_codes_remaining ?? 0) <= 2
                  ? "text-sev-medium"
                  : "text-muted-foreground"
              )}
            >
              {status.backup_codes_remaining} códigos de respaldo restantes
            </span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setRegenOpen(true)}
              data-testid="mfa-regenerate-btn"
              className="border border-primary/50 text-primary bg-primary/10 px-3 py-1.5 text-xs font-mono tracking-widest uppercase hover:bg-primary/20 inline-flex items-center gap-1"
            >
              <RefreshCw className="w-3 h-3" /> Regenerar códigos
            </button>
            <button
              onClick={() => setDisableOpen(true)}
              data-testid="mfa-disable-btn"
              className="border border-sev-critical/50 text-sev-critical bg-sev-critical/10 px-3 py-1.5 text-xs font-mono tracking-widest uppercase hover:bg-sev-critical/20"
            >
              Deshabilitar MFA
            </button>
          </div>
        </div>
      )}

      {/* Regenerate form */}
      {regenOpen && (
        <div className="border border-primary/40 bg-primary/5 p-4 space-y-3" data-testid="mfa-regen-form">
          <div className="overline text-primary flex items-center gap-2">
            <AlertTriangle className="w-3 h-3" /> Regenerar códigos invalidará TODOS los actuales
          </div>
          <p className="text-xs text-muted-foreground">
            Introduce tu contraseña y un código TOTP válido de tu app. Se generarán 10 nuevos códigos de un solo uso.
          </p>
          <input type="password" value={regenPw} onChange={(e) => setRegenPw(e.target.value)}
                 placeholder="Contraseña actual"
                 className="w-full bg-background border border-border px-3 py-2 text-sm focus:border-primary outline-none"
                 data-testid="mfa-regen-password" />
          <input value={regenCode} onChange={(e) => setRegenCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                 placeholder="Código de 6 dígitos"
                 className="w-32 bg-background border border-border px-3 py-2 text-sm font-mono tracking-widest text-center focus:border-primary outline-none"
                 data-testid="mfa-regen-code" />
          <div className="flex gap-2">
            <button onClick={regenerate} disabled={busy || !regenPw || regenCode.length !== 6}
                    data-testid="mfa-regen-confirm-btn"
                    className="bg-primary text-primary-foreground px-4 py-2 text-sm font-mono tracking-widest uppercase hover:bg-primary/90 disabled:opacity-50">
              {busy ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "Regenerar"}
            </button>
            <button onClick={() => { setRegenOpen(false); setRegenPw(""); setRegenCode(""); }}
                    className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground">Cancelar</button>
          </div>
        </div>
      )}

      {/* Disable form */}
      {disableOpen && (
        <div className="border border-sev-critical/40 bg-sev-critical/5 p-4 space-y-3" data-testid="mfa-disable-form">
          <div className="overline text-sev-critical">Confirma para deshabilitar</div>
          <input type="password" value={pw} onChange={(e) => setPw(e.target.value)}
                 placeholder="Contraseña actual"
                 className="w-full bg-background border border-border px-3 py-2 text-sm focus:border-primary outline-none"
                 data-testid="mfa-disable-password" />
          <input value={dCode} onChange={(e) => setDCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                 placeholder="Código de 6 dígitos"
                 className="w-32 bg-background border border-border px-3 py-2 text-sm font-mono tracking-widest text-center focus:border-primary outline-none"
                 data-testid="mfa-disable-code" />
          <div className="flex gap-2">
            <button onClick={disable} disabled={busy || !pw || dCode.length !== 6}
                    className="bg-sev-critical text-primary-foreground px-4 py-2 text-sm font-mono tracking-widest uppercase hover:bg-sev-critical/90 disabled:opacity-50">
              Deshabilitar
            </button>
            <button onClick={() => { setDisableOpen(false); setPw(""); setDCode(""); }}
                    className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground">Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
}
